import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ServiceOverviewProps {
  onBookClick: (service: string) => void;
}

interface ServiceItem {
  id: number;
  key: string;
  title: string;
  description: string;
  image: string;
  features: string[];
  mobileFeatures: string[];
  price: string;
  originalPrice: string;
  isJunior?: boolean;
}

export default function ServiceOverview({ onBookClick }: ServiceOverviewProps) {
  const { t } = useTranslation();
  const [modalService, setModalService] = useState<ServiceItem | null>(null);

  const services: ServiceItem[] = [
    {
      id: 1,
      key: 'kleiner',
      title: t('services.kleiner.title'),
      description: t('services.kleiner.description'),
      image: 'https://public.readdy.ai/ai/img_res/4099f41cb110554b1d2103d18ac9ec2f.jpg',
      features: [
        t('services.kleiner.feature1'),
        t('services.kleiner.feature2'),
        t('services.kleiner.feature3'),
        t('services.kleiner.feature4'),
        t('services.kleiner.feature5'),
      ],
      mobileFeatures: [
        t('services.kleiner.mobileFeature1'),
        t('services.kleiner.mobileFeature2'),
        t('services.kleiner.mobileFeature3'),
      ],
      price: '49',
      originalPrice: '59',
    },
    {
      id: 2,
      key: 'grosser',
      title: t('services.grosser.title'),
      description: t('services.grosser.description'),
      image: 'https://readdy.ai/api/search-image?query=professional%20ski%20service%20workshop%20interior%20with%20multiple%20skis%20being%20serviced%20on%20workbenches%2C%20technician%20performing%20complete%20ski%20overhaul%20and%20base%20repair%2C%20bright%20modern%20workshop%20with%20professional%20equipment%20and%20tools%2C%20clean%20organized%20workspace%2C%20detailed%20craftsmanship%20visible%2C%20warm%20professional%20lighting%20highlighting%20precision%20work&width=600&height=400&seq=ski-complete-overhaul-workshop&orientation=landscape',
      features: [
        t('services.grosser.feature1'),
        t('services.grosser.feature2'),
        t('services.grosser.feature3'),
        t('services.grosser.feature4'),
        t('services.grosser.feature5'),
        t('services.grosser.feature6'),
        t('services.grosser.feature7'),
      ],
      mobileFeatures: [
        t('services.grosser.mobileFeature1'),
        t('services.grosser.mobileFeature2'),
        t('services.grosser.mobileFeature3'),
        t('services.grosser.mobileFeature4'),
      ],
      price: '69',
      originalPrice: '79',
    },
    {
      id: 3,
      key: 'renn',
      title: t('services.renn.title'),
      description: t('services.renn.description'),
      image: 'https://readdy.ai/api/search-image?query=dynamic%20professional%20ski%20racer%20in%20racing%20position%20speeding%20down%20snowy%20slope%20during%20competition%2C%20intense%20focused%20action%20shot%2C%20racing%20bib%20number%20visible%2C%20sharp%20skis%20cutting%20through%20snow%20creating%20spray%2C%20blurred%20motion%20background%20showing%20speed%20and%20performance%2C%20dramatic%20winter%20sports%20photography%20capturing%20elite%20athletic%20performance%20and%20precision&width=600&height=400&seq=ski-racing-top-performance&orientation=landscape',
      features: [
        t('services.renn.feature1'),
        t('services.renn.feature2'),
        t('services.renn.feature3'),
        t('services.renn.feature4'),
        t('services.renn.feature5'),
        t('services.renn.feature6'),
        t('services.renn.feature7'),
        t('services.renn.feature8'),
      ],
      mobileFeatures: [
        t('services.renn.mobileFeature1'),
        t('services.renn.mobileFeature2'),
        t('services.renn.mobileFeature3'),
        t('services.renn.mobileFeature4'),
      ],
      price: '79',
      originalPrice: '89',
    },
    {
      id: 0,
      key: 'junior',
      title: t('services.junior.title'),
      description: t('services.junior.description'),
      image: 'https://readdy.ai/api/search-image?query=cheerful%20child%20holding%20small%20colorful%20skis%20in%20snowy%20alpine%20resort%2C%20bright%20winter%20sunshine%2C%20child%20wearing%20colorful%20ski%20jacket%20and%20helmet%2C%20white%20snowy%20background%20with%20soft%20bokeh%2C%20joyful%20winter%20sports%20scene%20for%20kids%2C%20clean%20minimal%20snowy%20landscape%2C%20warm%20inviting%20atmosphere&width=600&height=400&seq=junior-ski-kids-service&orientation=landscape',
      features: [
        t('services.junior.feature1'),
        t('services.junior.feature2'),
        t('services.junior.feature3'),
        t('services.junior.feature4'),
        t('services.junior.feature5'),
      ],
      mobileFeatures: [
        t('services.junior.mobileFeature1'),
        t('services.junior.mobileFeature2'),
        t('services.junior.mobileFeature3'),
      ],
      price: '29',
      originalPrice: '40',
      isJunior: true,
    },
  ];

  const handleCardClick = (service: ServiceItem) => {
    setModalService(service);
  };

  const closeModal = () => {
    setModalService(null);
  };

  const handleBookFromModal = () => {
    if (modalService) {
      closeModal();
      onBookClick(modalService.key);
    }
  };

  return (
    <>
      <section id="service" className="flex-1 flex flex-col justify-center bg-[#F8F6F0] py-2 sm:py-3 md:py-4 scroll-mt-16 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 w-full">
          <div className="mb-3 sm:mb-3 md:mb-4">
            <h2 className="text-lg sm:text-xl md:text-3xl font-black text-gray-900">
              {t('services.sectionTitle')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
            {services.map((service) => (
              <div
                key={service.id}
                className={`rounded-xl md:rounded-2xl shadow-md hover:shadow-xl transition-all bg-white overflow-hidden ${
                  service.id === 2
                    ? 'border-2 md:border-4 border-[#2B8FBF]'
                    : service.isJunior
                    ? 'border-2 border-[#D4FF00]'
                    : ''
                }`}
              >
                <div
                  className="flex md:block cursor-pointer md:cursor-default"
                  onClick={() => {
                    if (window.innerWidth < 768) handleCardClick(service);
                  }}
                >
                  <div className="w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0 md:w-full md:h-32 overflow-hidden rounded-tl-xl rounded-bl-xl md:rounded-t-2xl md:rounded-bl-none relative">
                    <img
                      src={service.image}
                      alt={service.title}
                      width="600"
                      height="128"
                      loading="lazy"
                      className="w-full h-full object-cover object-center"
                    />
                    {service.id === 2 && (
                      <div className="absolute top-1 left-1 bg-[#2B8FBF] text-white text-[7px] font-black px-1 py-0.5 rounded-full leading-none">
                        EMPFOHLEN
                      </div>
                    )}
                    {service.isJunior && (
                      <div className="absolute top-1.5 left-1.5 bg-[#D4FF00] text-gray-900 text-[9px] font-black px-1.5 py-0.5 rounded-full">
                        KIDS
                      </div>
                    )}
                  </div>

                  <div className="p-2 sm:p-2.5 md:p-3 text-gray-900 flex-1 min-w-0">
                    <h3 className="text-xs sm:text-sm leading-tight md:text-sm font-bold mb-1 md:mb-1.5 line-clamp-2">
                      {service.title}
                    </h3>

                    <p className="text-gray-600 mb-2 md:mb-2 leading-relaxed text-xs hidden md:block line-clamp-2">
                      {service.description}
                    </p>

                    {/* Mobile: Features links, Preis+Buttons rechts */}
                    <div className="flex gap-2 md:hidden">
                      {/* Linke Spalte: Feature-Liste */}
                      <div className="flex-1 min-w-0">
                        <ul className="space-y-0.5">
                          {service.mobileFeatures.map((feature, index) => (
                            <li key={index} className="flex items-start gap-1">
                              <i className="ri-check-line text-[#2B8FBF] text-[10px] mt-0.5 flex-shrink-0"></i>
                              <span className="text-[9px] sm:text-[10px] text-gray-700 leading-snug line-clamp-1">{feature}</span>
                            </li>
                          ))}
                        </ul>
                        {/* Junior-Hinweis */}
                        {service.isJunior && (
                          <div className="flex items-center gap-1 mt-1">
                            <i className="ri-ruler-line text-[#D4A800] text-[10px] flex-shrink-0"></i>
                            <span className="text-[9px] sm:text-[10px] text-[#D4A800] font-semibold leading-snug">{t('services.juniorLengthNote')}</span>
                          </div>
                        )}
                      </div>

                      {/* Rechte Spalte: Preis + Buttons */}
                      <div className="flex-shrink-0 flex flex-col items-end justify-between gap-1.5">
                        <div className="text-right">
                          <div className="flex items-baseline gap-0.5 justify-end">
                            <span className="text-[9px] sm:text-[10px] font-bold text-gray-900 line-through decoration-red-600">{service.originalPrice}</span>
                            <span className="text-xs sm:text-sm font-black text-red-600">{service.price}</span>
                            <span className="text-[9px] sm:text-[10px] text-red-600">CHF</span>
                          </div>
                          <p className="text-[7px] sm:text-[8px] text-red-600 whitespace-nowrap">{t('services.discount')}</p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleCardClick(service); }}
                            className="px-2 py-0.5 rounded-full text-[9px] sm:text-[10px] font-semibold border border-gray-300 text-gray-500 hover:border-[#2B8FBF] hover:text-[#2B8FBF] transition-all cursor-pointer whitespace-nowrap"
                          >
                            Details
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); onBookClick(service.key); }}
                            className="px-2 py-0.5 rounded-full text-[9px] sm:text-[10px] font-bold bg-[#2B8FBF] text-white hover:bg-[#2480ad] transition-all cursor-pointer whitespace-nowrap"
                          >
                            {t('services.mobileBookButton')}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Desktop Feature-Liste */}
                    <ul className="space-y-1 mb-2 hidden md:block">
                      {service.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-1.5">
                          <i className="ri-check-line text-[#2B8FBF] text-sm mt-0.5 flex-shrink-0"></i>
                          <span className="text-[11px]">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {/* Desktop Junior-Hinweis */}
                    {service.isJunior && (
                      <div className="hidden md:flex items-center gap-1.5 bg-yellow-50 border border-yellow-200 rounded-lg px-2 py-1.5 mb-2">
                        <i className="ri-ruler-line text-yellow-600 text-sm flex-shrink-0"></i>
                        <span className="text-[11px] text-yellow-700 font-semibold leading-snug">{t('services.juniorLengthNote')}</span>
                      </div>
                    )}

                    <div className="pt-0 md:pt-1.5 md:border-t md:border-gray-200">
                      {/* Desktop Preis + Button */}
                      <div className="hidden md:flex items-center justify-between gap-1.5 sm:gap-2">
                        <div className="min-w-0 flex-shrink">
                          <div className="flex items-baseline gap-0.5 sm:gap-1">
                            <span className="text-[9px] sm:text-[10px] md:text-xs font-bold text-gray-900 line-through decoration-red-600">{service.originalPrice} CHF</span>
                            <span className="text-xs sm:text-sm md:text-xl font-black text-red-600">{service.price}</span>
                            <span className="text-[9px] sm:text-[10px] md:text-sm text-red-600">CHF</span>
                          </div>
                          <p className="text-[7px] sm:text-[8px] md:text-[10px] text-red-600 whitespace-nowrap">{t('services.discount')}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => onBookClick(service.key)}
                        className={`hidden md:block w-full mt-2 py-2 rounded-full text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                          service.isJunior
                            ? 'bg-[#D4FF00] text-gray-900 hover:bg-[#c4ef00]'
                            : 'bg-white border-2 border-[#2B8FBF] text-[#2B8FBF] hover:bg-[#2B8FBF] hover:text-white'
                        }`}
                      >
                        {t('services.bookButton')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mobile Service Detail Modal */}
      {modalService && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center md:hidden"
          onClick={closeModal}
        >
          <div className="absolute inset-0 bg-black/50" />
          <div
            className="relative w-full bg-white rounded-t-2xl overflow-hidden shadow-2xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative w-full h-28 flex-shrink-0">
              <img
                src={modalService.image}
                alt={modalService.title}
                width="600"
                height="112"
                loading="lazy"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              <button
                onClick={closeModal}
                className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center bg-white/90 rounded-full shadow-md cursor-pointer"
                aria-label={t('common.close')}
              >
                <i className="ri-close-line text-gray-800 text-base"></i>
              </button>
              <div className="absolute bottom-2 left-3">
                <h3 className="text-white font-black text-base leading-tight drop-shadow">
                  {modalService.title}
                </h3>
              </div>
            </div>

            <div className="overflow-y-auto flex-1 px-4 pt-3 pb-2">
              <p className="text-xs text-gray-600 leading-relaxed mb-2.5">
                {modalService.description}
              </p>

              {/* Junior-Hinweis im Modal */}
              {modalService.isJunior && (
                <div className="flex items-center gap-2 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2 mb-2.5">
                  <i className="ri-ruler-line text-yellow-600 text-sm flex-shrink-0"></i>
                  <span className="text-xs text-yellow-700 font-semibold">{t('services.juniorLengthNote')}</span>
                </div>
              )}

              <div className="mb-2">
                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1.5">{t('services.modalFeaturesLabel')}</p>
                <ul className="space-y-1">
                  {modalService.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-[#2B8FBF] text-xs"></i>
                      </div>
                      <span className="text-xs text-gray-800 leading-snug">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="px-4 py-3 border-t border-gray-100 bg-white flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  <div className="flex items-baseline gap-1">
                    <span className="text-xs font-bold text-gray-400 line-through decoration-red-500">{modalService.originalPrice} CHF</span>
                    <span className="text-lg font-black text-red-600">{modalService.price}</span>
                    <span className="text-xs text-red-600 font-semibold">CHF</span>
                  </div>
                  <p className="text-[9px] text-red-500 font-medium">{t('services.discount')}</p>
                </div>
                <button
                  onClick={handleBookFromModal}
                  className="flex-1 py-3 rounded-full text-sm font-black tracking-wide bg-[#2B8FBF] text-white hover:bg-[#2480ad] transition-all cursor-pointer whitespace-nowrap shadow-md"
                >
                  {t('services.modalBookButton')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
